import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import { InlineAlert } from '@/components/app/Modal';
import { useDialogs } from '@/components/app/DialogProvider';
import { useCurrentUser } from '@/hooks/useAuth';
import { authApi, readApiError } from '@/lib/auth';
import { AvatarUploader } from '@/components/ui/AvatarUploader';

/**
 * Account-level profile editor. Lives outside any workspace context
 * because the data it controls (display name, avatar, sign-in linkage)
 * is per-user, not per-workspace. The workspace-scoped Settings page
 * still owns AI usage cap, plan, etc.
 */
// Curated IANA zones covering the target first-world markets + majors.
const TIMEZONES = [
  'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata', 'Asia/Dubai',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid', 'Europe/Lisbon', 'Europe/Moscow',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo',
  'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

export function ProfilePage() {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();
  const qc = useQueryClient();
  const { confirm } = useDialogs();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.name ?? '');
      setTimezone(user.timezone ?? '');
    }
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      authApi.updateProfile({
        name: name.trim() === '' ? undefined : name.trim(),
        timezone,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(['auth', 'me'], updated);
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2400);
    },
    onError: (err) => setError(readApiError(err, 'Failed to save profile.')),
  });

  const exportData = useMutation({
    mutationFn: () => authApi.exportMyData(),
    onError: (err) => setError(readApiError(err, 'Failed to export data.')),
  });

  const deleteAccount = useMutation({
    mutationFn: () => authApi.deleteMyAccount(),
    onSuccess: () => { window.location.href = '/'; },
    onError: (err) => setError(readApiError(err, 'Failed to delete account.')),
  });

  const confirmDelete = async () => {
    const ok = await confirm({
      title: t('profile.delete_confirm', {
        defaultValue: 'Delete your account? Your personal data is anonymized and you will be signed out. This cannot be undone.',
      }) as string,
      confirmLabel: t('common.delete', { defaultValue: 'Delete' }) as string,
    });
    if (ok) deleteAccount.mutate();
  };

  if (isLoading) {
    return (
      <AppShell title={t('profile.title', { defaultValue: 'Profile' })} maxWidth="max-w-2xl">
        <p className="text-sm text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>
      </AppShell>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <AppShell
      title={t('profile.title', { defaultValue: 'Profile' })}
      description={t('profile.subtitle', { defaultValue: 'Your account, sign-in methods, and how you appear to teammates.' }) as string}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Identity + avatar upload */}
        <section className="card-paper p-6 space-y-5">
          <AvatarUploader user={user} />
          <div className="border-t border-ink-100 dark:border-ink-700 pt-3">
            <div className="text-sm text-ink-700 dark:text-ink-200 truncate">{user.email}</div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] uppercase tracking-[0.12em] text-ink-500">
              {user.emailVerified ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <Dot tone="emerald" />
                  {t('profile.email_verified', { defaultValue: 'Email verified' })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-warn-600">
                  <Dot tone="warn" />
                  {t('profile.email_unverified', { defaultValue: 'Email not verified' })}
                </span>
              )}
              {user.isSiteAdmin && (
                <span className="inline-flex items-center gap-1 text-accent-700">
                  <Dot tone="accent" />
                  {t('profile.site_admin', { defaultValue: 'Site admin' })}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Edit form */}
        <form onSubmit={onSubmit} className="card-paper p-6 space-y-4">
          <h2 className="display text-[16px] font-semibold text-ink-900">
            {t('profile.basics', { defaultValue: 'Basics' })}
          </h2>

          <Field
            id="display-name"
            label={t('profile.display_name', { defaultValue: 'Display name' })}
            hint={t('profile.display_name_hint', { defaultValue: 'How teammates see you in comments, mentions, and audit logs.' }) as string}
          >
            <input
              id="display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="w-full h-10 rounded-lg border border-ink-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 dark:bg-ink-800 dark:border-ink-700 dark:text-ink-100"
            />
          </Field>

          <Field
            id="timezone"
            label={t('profile.timezone', { defaultValue: 'Timezone' })}
            hint={t('profile.timezone_hint', { defaultValue: 'How dates and times are shown to you. Leave on browser default to follow your device.' }) as string}
          >
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full h-10 rounded-lg border border-ink-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 dark:bg-ink-800 dark:border-ink-700 dark:text-ink-100"
            >
              <option value="">{t('profile.tz_browser', { defaultValue: 'Browser default' })}</option>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>

          {error && <InlineAlert tone="error">{error}</InlineAlert>}
          {saved && <InlineAlert tone="success">{t('profile.saved', { defaultValue: 'Saved.' })}</InlineAlert>}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={save.isPending}
              className="btn-press px-4 py-2 text-sm disabled:opacity-50"
            >
              {save.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save_changes', { defaultValue: 'Save changes' })}
            </button>
          </div>
        </form>

        {/* Sign-in methods */}
        <section className="card-paper p-6 space-y-3">
          <h2 className="display text-[16px] font-semibold text-ink-900">
            {t('profile.sign_in_methods', { defaultValue: 'Sign-in methods' })}
          </h2>
          <ul className="space-y-1.5 text-sm">
            <MethodRow active={user.hasPassword} label={t('profile.password', { defaultValue: 'Email & password' })} />
            <MethodRow active={user.linked.google} label="Google" />
            <MethodRow active={user.linked.microsoft} label="Microsoft" />
            <MethodRow active={user.linked.github} label="GitHub" />
            <MethodRow active={user.linked.apple} label="Apple" />
          </ul>
        </section>

        {/* Your data & privacy — GDPR/APPI right of access + erasure */}
        <section className="card-paper p-6 space-y-3">
          <h2 className="display text-[16px] font-semibold text-ink-900">
            {t('profile.data_privacy', { defaultValue: 'Your data & privacy' })}
          </h2>
          <p className="text-[13px] text-ink-500 dark:text-ink-400">
            {t('profile.data_privacy_hint', {
              defaultValue: 'Download everything we hold about you, or close your account. See the Privacy Policy and DPA for how your data is handled.',
            })}
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <button
              type="button"
              onClick={() => exportData.mutate()}
              disabled={exportData.isPending}
              className="btn-ghost px-4 py-2 text-sm disabled:opacity-50"
            >
              {exportData.isPending
                ? t('common.loading', { defaultValue: 'Loading…' })
                : t('profile.export_data', { defaultValue: 'Export my data' })}
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteAccount.isPending}
              className="px-4 py-2 text-sm rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30"
            >
              {deleteAccount.isPending
                ? t('common.loading', { defaultValue: 'Loading…' })
                : t('profile.delete_account', { defaultValue: 'Delete account' })}
            </button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Field({
  id, label, hint, children,
}: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink-900 mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[12px] text-ink-500 dark:text-ink-400">{hint}</p>}
    </div>
  );
}

function MethodRow({ active, label }: { active: boolean; label: string }) {
  return (
    <li className="flex items-center justify-between text-ink-700 dark:text-ink-200">
      <span>{label}</span>
      <span className={active ? 'text-emerald-700 text-[11.5px] font-semibold' : 'text-ink-400 text-[11.5px]'}>
        {active ? 'Linked' : '—'}
      </span>
    </li>
  );
}

function Dot({ tone }: { tone: 'emerald' | 'warn' | 'accent' }) {
  const color =
    tone === 'emerald' ? 'bg-emerald-500' : tone === 'warn' ? 'bg-warn-500' : 'bg-accent-500';
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${color}`} />;
}
